import React from 'react';
import styled from 'styled-components';
import WalletButtons from '../../../common/WalletButtons';

const ConfirmationContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
  background: linear-gradient(180deg, #ffffff, #fafafa);
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  
  @media (max-width: 576px) {
    padding: 2rem 1.25rem;
    border-radius: 12px;
  }
  
  @media print {
    display: none !important;
  }
`;

const SuccessIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #2ecc71, #27ae60);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 3rem;
  font-weight: bold;
  box-shadow: 0 8px 24px rgba(46, 204, 113, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    border: 2px solid rgba(46, 204, 113, 0.2);
    animation: pulse 2s ease-out infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
  
  @media (max-width: 576px) {
    width: 80px;
    height: 80px;
    font-size: 2.5rem;
    margin-bottom: 1.25rem;
    
    &::after {
      width: 96px;
      height: 96px;
    }
  }
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  font-weight: 700;
  
  @media (max-width: 576px) {
    font-size: 1.75rem;
  }
`;

const SuccessMessage = styled.p`
  font-size: 1.15rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2.5rem;
  line-height: 1.7;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const FestiveBanner = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  color: #fff;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin: 1rem auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-weight: 600;
  font-size: 1.1rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
`;

const FestiveNote = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid ${props => props.theme.colors.accent};
  color: ${props => props.theme.colors.primary};
  border-radius: 12px;
  padding: 2rem;
  margin: 0 auto 2rem;
  max-width: 840px;
  text-align: left;
`;

const DuchessMessage = styled.blockquote`
  margin: 0;
  padding: 1.25rem 1.5rem;
  border-left: 4px solid ${props => props.theme.colors.accent};
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: 8px;
  font-style: italic;
  line-height: 1.7;
`;

const BookingReference = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  color: white;
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-10%, -10%);
    }
  }
`;

const ReferenceNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-top: 0.75rem;
  letter-spacing: 3px;
  font-family: 'Courier New', monospace;
`;

const DetailsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: left;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  
  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: ${props => props.theme.colors.accent};
    border-radius: 2px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const TicketsList = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const TicketItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TicketInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TicketName = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const TicketQuantity = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
`;

const TicketPrice = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  font-size: 1.1rem;
`;

const TotalSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  border: 2px solid ${props => props.theme.colors.accent};
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
    border-radius: 12px 12px 0 0;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.2rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid ${props => props.theme.colors.accent};
  }
`;

const TotalLabel = styled.span`
  color: ${props => props.theme.colors.secondary};
`;

const TotalValue = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  margin-top: 2rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

// Removed primary action button (download not needed)

const SecondaryButton = styled.button`
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 6px;
  padding: 0.625rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const SavingsBanner = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #1a2d5a);
  color: white;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  margin-bottom: 2.5rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(16, 29, 64, 0.3);
  position: relative;
  overflow: hidden;
  border: 3px solid #2ecc71;
  
  &::before {
    content: '🎄';
    position: absolute;
    font-size: 8rem;
    opacity: 0.08;
    top: -2rem;
    right: -1rem;
    transform: rotate(15deg);
  }
  
  &::after {
    content: '🎁';
    position: absolute;
    font-size: 6rem;
    opacity: 0.08;
    bottom: -1rem;
    left: -1rem;
    transform: rotate(-15deg);
  }
`;

const SavingsBadge = styled.div`
  display: inline-block;
  background: #2ecc71;
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(46, 204, 113, 0.4);
`;

const SavingsTitle = styled.h2`
  font-size: 1.6rem;
  margin: 0 0 1rem 0;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.95);
`;

const SavingsAmount = styled.div`
  font-size: 4rem;
  font-weight: 800;
  margin: 1.5rem 0;
  color: #2ecc71;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  display: inline-block;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 4px;
    background: #2ecc71;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(46, 204, 113, 0.5);
  }
`;

const SavingsSubtext = styled.p`
  font-size: 1.3rem;
  margin: 2rem 0 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const SavingsDetails = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const SavingsDetail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  min-width: 160px;
  backdrop-filter: blur(10px);
`;

const SavingsDetailLabel = styled.span`
  font-size: 0.85rem;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(255, 255, 255, 0.8);
`;

const SavingsDetailValue = styled.span`
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2.5rem;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
    border-radius: 12px;
    z-index: -1;
    opacity: 0.1;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  font-weight: 500;
`;

const BookingConfirmation = ({ 
  bookingReference, 
  selectedTickets, 
  selectedDate, 
  selectedTimeSlot, 
  customerDetails, 
  totalAmount,
  venposBasket,
  bookingSummary,
  isFamilySaver = false,
  pageId
}) => {
  // Detect if this is a membership journey
  const isMembershipJourney = selectedTickets && selectedTickets.some(ticket => ticket.webSaleItemTypeID === 4);
  const isFlowerGardenShow = pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
  const familySaverLabel = isFlowerGardenShow ? 'Flower and Garden Show 2026' : 'Family Saver';
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Handle both Date objects and date strings
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot?.time || timeSlot || 'Not specified';
  };

  // Extract discount information from venposBasket OR bookingSummary
  // IMPORTANT: totalAmount is the FINAL amount paid (after discounts)
  const basketData = venposBasket?.data;
  
  // Try to get discount data from basket first, then from bookingSummary
  const discounts = basketData?.discounts || bookingSummary?.discounts || [];
  const totalSavings = bookingSummary?.totalSavings || discounts.reduce((sum, discount) => sum + (discount.discountedAmount || 0), 0);
  const hasSavings = totalSavings > 0;
  
  // Check if any discount code starts with "FS" (Family Saver) or "TZ" (Travel Zoo)
  const isFamilySaverDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('FS'));
  const isTravelZooDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('TZ'));
  
  // Calculate prices - prefer bookingSummary (preserved data) over basketData
  let originalPrice, finalPrice;
  if (bookingSummary?.originalPrice && bookingSummary?.finalPrice !== undefined) {
    // Use preserved pricing from bookingSummary
    originalPrice = bookingSummary.originalPrice;
    finalPrice = bookingSummary.finalPrice;
  } else if (basketData?.totalPrice && basketData?.finalPrice !== undefined) {
    // Fall back to current basket data
    originalPrice = basketData.totalPrice;
    finalPrice = basketData.finalPrice;
  } else {
    // Last resort - totalAmount is the final price
    originalPrice = totalAmount;
    finalPrice = totalAmount;
  }
  
  const savingsPercentage = originalPrice > 0 ? ((totalSavings / originalPrice) * 100).toFixed(0) : 0;
  
  // Debug logging

  // CRITICAL FIX: Get individual tickets from venposBasket instead of grouped selectedTickets
  // The venposBasket contains the actual individual tickets (each with quantity 1) that Venpos created
  // MUST be declared BEFORE being used in bookingDataForWallet
  const basketGroups = basketData?.groups || [];
  const basketItems = basketGroups.flatMap(g => g.items || []);
  
  // Debug: Log basket structure
  console.log('🎫 [BookingConfirmation] Basket Data:', {
    basketGroups: basketGroups.length,
    basketItems: basketItems.length,
    basketData: basketData,
    items: basketItems.map(item => ({
      name: item.name,
      unitPrice: item.unitPrice,
      price: item.price,
      totalPrice: item.totalPrice,
      discountedAmount: item.discountedAmount,
      quantity: item.quantity
    }))
  });
  
  // CRITICAL FIX: Group basket items for DISPLAY purposes
  // Venpos sends individual items, but we want to display them grouped
  const ticketsToDisplay = basketItems.length > 0 
    ? (() => {
        // Group items by webSaleItemId and name
        const groupedMap = new Map();
        
        basketItems.forEach(item => {
          const key = `${item.webSaleItemId}-${item.name}`;
          const itemQuantity = item.quantity || 1;
          const unitPrice = item.unitPrice || (item.totalPrice / itemQuantity) || item.price || 0;
          
          if (groupedMap.has(key)) {
            // Add to existing group
            const existing = groupedMap.get(key);
            existing.quantity += itemQuantity;
            existing.totalPrice += item.totalPrice || (unitPrice * itemQuantity);
          } else {
            // Create new group
            groupedMap.set(key, {
              webSaleItemId: item.webSaleItemId,
              name: item.name || item.description,
              quantity: itemQuantity,
              unitPrice: unitPrice,
              totalPrice: item.totalPrice || (unitPrice * itemQuantity)
            });
          }
        });
        
        return Array.from(groupedMap.values());
      })()
    : (() => {
        // Fallback: Group selectedTickets for display
        const groupedMap = new Map();
        
        selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {
          const key = `${ticket.webSaleItemId}-${ticket.name}`;
          
          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key);
            existing.quantity += ticket.quantity;
            existing.totalPrice += ticket.price * ticket.quantity;
          } else {
            groupedMap.set(key, {
              webSaleItemId: ticket.webSaleItemId,
              name: ticket.name,
              quantity: ticket.quantity,
              unitPrice: ticket.price,
              totalPrice: ticket.price * ticket.quantity
            });
          }
        });
        
        return Array.from(groupedMap.values());
      })();
  
  console.log('🎫 [BookingConfirmation] Tickets to Display (Grouped):', ticketsToDisplay);

  // Removed download/print handlers - email confirmation contains ticket details

  const handleReturnHome = () => {
    window.location.href = '/';
  };

  // Prepare booking data for Apple Wallet
  const bookingDataForWallet = {
    bookingReference,
    eventName: isMembershipJourney ? 'Belvoir Castle Membership' : 'Belvoir Castle Visit',
    selectedDate,
    selectedTimeSlot,
    selectedTickets: ticketsToDisplay, // Use grouped tickets for display
    customerDetails,
    totalAmount
  };

  const handleWalletSuccess = (passBlob, filename) => {

  };

  const handleWalletError = (error) => {

  };

  // Prices are VAT inclusive - no separate VAT line needed

  return (
    <ConfirmationContainer className="no-print">
      <SuccessIcon>
        ✓
      </SuccessIcon>
      
      <SuccessTitle>Booking Confirmed!</SuccessTitle>
      <SuccessMessage>
        {isMembershipJourney 
          ? `Thank you for your membership purchase. Your membership to Belvoir Castle has been confirmed.`
          : `Thank you for your booking. Your visit to Belvoir Castle has been confirmed.`
        }
        A confirmation email has been sent to {customerDetails.email}.
      </SuccessMessage>

      {isFamilySaver && !isMembershipJourney && (
        <>
          <FestiveBanner>
            Belvoir Castle — Supporting Families this Christmas
          </FestiveBanner>
          <FestiveNote>
            <DuchessMessage>
              A heartfelt thank you from the Duchess and her team — we look forward to welcoming you to Belvoir Castle this Christmas. May your visit be full of joy, wonder and magical memories.
            </DuchessMessage>
          </FestiveNote>
        </>
      )}

      <BookingReference>
        <div>Booking Reference</div>
        <ReferenceNumber>{bookingReference}</ReferenceNumber>
      </BookingReference>

      {hasSavings && (
        <SavingsBanner>
          <SavingsBadge>
            {isFamilySaverDiscount 
              ? (isFlowerGardenShow ? 'Flower and Garden Show 2026' : 'FAMILY SAVER') 
              : isTravelZooDiscount 
                ? 'TRAVEL ZOO' 
                : 'SPECIAL OFFER'}
          </SavingsBadge>
          <SavingsTitle>
            {isFamilySaverDiscount 
              ? `You've Saved with our ${familySaverLabel} Offer!` 
              : isTravelZooDiscount
                ? 'You\'ve Saved with Travel Zoo!'
                : 'Discount Applied to Your Booking'}
          </SavingsTitle>
          <SavingsAmount>£{totalSavings.toFixed(2)}</SavingsAmount>
          <SavingsSubtext>
            {isMembershipJourney 
              ? `You saved ${savingsPercentage}% on your membership to Belvoir Castle`
              : `You saved ${savingsPercentage}% on your visit to Belvoir Castle`
            }
          </SavingsSubtext>
          <SavingsDetails>
            <SavingsDetail>
              <SavingsDetailLabel>Original Price</SavingsDetailLabel>
              <SavingsDetailValue>£{originalPrice.toFixed(2)}</SavingsDetailValue>
            </SavingsDetail>
            <SavingsDetail>
              <SavingsDetailLabel>You Paid</SavingsDetailLabel>
              <SavingsDetailValue>£{finalPrice.toFixed(2)}</SavingsDetailValue>
            </SavingsDetail>
          </SavingsDetails>
        </SavingsBanner>
      )}

      {!isMembershipJourney && (
        <DetailsSection>
          <SectionTitle>
            Visit Details
          </SectionTitle>
          
          <DetailsGrid>
            <DetailItem>
              <DetailLabel>Visit Date</DetailLabel>
              <DetailValue>{formatDate(selectedDate)}</DetailValue>
            </DetailItem>
            
            <DetailItem>
              <DetailLabel>Visit Time</DetailLabel>
              <DetailValue>{formatTime(selectedTimeSlot)}</DetailValue>
            </DetailItem>
            
            <DetailItem>
              <DetailLabel>Total Tickets</DetailLabel>
              <DetailValue>
                {ticketsToDisplay.reduce((sum, ticket) => sum + ticket.quantity, 0)}
              </DetailValue>
            </DetailItem>
          </DetailsGrid>
        </DetailsSection>
      )}
      
      {isMembershipJourney && (
        <DetailsSection>
          <SectionTitle>
            Membership Details
          </SectionTitle>
          
          <DetailsGrid>
            <DetailItem>
              <DetailLabel>Total Memberships</DetailLabel>
              <DetailValue>
                {ticketsToDisplay.reduce((sum, ticket) => sum + ticket.quantity, 0)}
              </DetailValue>
            </DetailItem>
          </DetailsGrid>
        </DetailsSection>
      )}

      <DetailsSection>
        <SectionTitle>
          Contact Information
        </SectionTitle>
        
        <DetailsGrid>
          <DetailItem>
            <DetailLabel>Name</DetailLabel>
            <DetailValue>{customerDetails.firstName} {customerDetails.lastName}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Email</DetailLabel>
            <DetailValue>{customerDetails.email}</DetailValue>
          </DetailItem>
          
          {customerDetails.phone && (
            <DetailItem>
              <DetailLabel>Phone</DetailLabel>
              <DetailValue>{customerDetails.phone}</DetailValue>
            </DetailItem>
          )}
        </DetailsGrid>
      </DetailsSection>

      <DetailsSection>
        <SectionTitle>
          {isMembershipJourney ? 'Memberships' : 'Tickets'}
        </SectionTitle>
        
        <TicketsList>
          {ticketsToDisplay.map((ticket, index) => {
              // Display total price for the group
              const displayPrice = ticket.totalPrice || 0;
              
              return (
                <TicketItem key={`${ticket.webSaleItemId}-${index}`}>
                  <TicketInfo>
                    <TicketName>{ticket.name}</TicketName>
                    <TicketQuantity>x{ticket.quantity}</TicketQuantity>
                  </TicketInfo>
                  <TicketPrice>£{displayPrice.toFixed(2)}</TicketPrice>
                </TicketItem>
              );
            })}
        </TicketsList>
      </DetailsSection>

      <TotalSection>
        {hasSavings ? (
          <>
            <TotalRow>
              <TotalLabel>Original Price</TotalLabel>
              <TotalValue>£{originalPrice.toFixed(2)}</TotalValue>
            </TotalRow>
            
            {discounts.map((discount, index) => {
              const code = discount.code ? discount.code.toUpperCase() : '';
              let discountLabel;
              
              // For membership journeys, don't show the actual code
              if (isMembershipJourney) {
                discountLabel = 'Discount code applied';
              } else if (code.startsWith('FS')) {
                discountLabel = 'Family Saver Discount';
              } else if (code.startsWith('TZ')) {
                discountLabel = 'Travel Zoo Discount';
              } else {
                discountLabel = `${discount.code} Discount`;
              }
              
              return (
                <TotalRow key={index}>
                  <TotalLabel style={{ color: '#2ecc71', fontWeight: '600' }}>
                    {discountLabel}
                  </TotalLabel>
                  <TotalValue style={{ color: '#2ecc71' }}>
                    -£{(discount.discountedAmount || 0).toFixed(2)}
                  </TotalValue>
                </TotalRow>
              );
            })}
            
            <TotalRow>
              <TotalLabel>Total Paid (inc. VAT)</TotalLabel>
              <TotalValue>£{finalPrice.toFixed(2)}</TotalValue>
            </TotalRow>
          </>
        ) : (
          <TotalRow>
            <TotalLabel>Total Paid (inc. VAT)</TotalLabel>
            <TotalValue>£{finalPrice.toFixed(2)}</TotalValue>
          </TotalRow>
        )}
      </TotalSection>

      <ActionButtons>
        {!isMembershipJourney && (
          <WalletButtons 
            bookingData={bookingDataForWallet}
            selectedTickets={ticketsToDisplay}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            customerDetails={customerDetails}
            totalAmount={totalAmount}
            venposBasket={venposBasket}
            bookingSummary={bookingSummary}
            isFamilySaver={isFamilySaver}
            onSuccess={handleWalletSuccess}
            onError={handleWalletError}
          />
        )}
        <SecondaryButton onClick={handleReturnHome}>
          Return Home
        </SecondaryButton>
      </ActionButtons>

      <InfoBox>
        <InfoText>
          {isMembershipJourney 
            ? `Please check your email for your membership confirmation and details. Your membership card and further information will be sent to you separately.`
            : `Please check your email for your booking confirmation and ticket details. You can also add your tickets to Apple Wallet or Google Wallet using the buttons above for easy access on your visit.`
          }
        </InfoText>
      </InfoBox>
    </ConfirmationContainer>
  );
};

export default BookingConfirmation;